import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import {
	MarkerRedesign,
	MarkerChange,
	MarkerContactedRedesign,
	MarkerNotContactedHover,
	MarkerContactedHover,
	ClearIcon
} from '@/assets/images';
import type {
	BusinessQuery,
	Business
} from '@/modules/dashboard/dashboardTypes';
import { fromJS } from 'immutable';
import Map from '@/utils/map';
import { Feature, inside, MultiPolygon, Polygon } from '@turf/turf';
import { useMapboxKeyQuery } from '@/hooks/useMapBoxKeyQuery';

interface MapProps {
	selectedView: 'businesses' | 'map';
	query: BusinessQuery;
	businesses: Business[];
	mapBoxKey: string;
	setQuery: (query: BusinessQuery) => void;
	createPopupContent: (
		business: BusinessQuery,
		onClose: () => void
	) => HTMLElement;
	mapBoxRef: React.RefObject<HTMLDivElement>;
	mapInstance: React.RefObject<mapboxgl.Map | null>;
	currentPopup: mapboxgl.Popup | null;
	mapStateRef: React.RefObject<{ zoom: number; center: [number, number] }>;
	previousCoordinates: React.RefObject<string | null>;
}

const SearchBusinessesMap: React.FC<MapProps> = ({
	selectedView,
	query,
	businesses,
	setQuery,
	createPopupContent,
	mapBoxRef,
	mapInstance,
	currentPopup,
	mapStateRef,
	previousCoordinates
}) => {
	const { data: mapboxKeyData } = useMapboxKeyQuery();
	const mapboxAPIKey = mapboxKeyData?.data?.data?.mapBoxAPIKey;
	const blueMarkerRef = useRef<mapboxgl.Marker | null>(null);
	const [activeMapInstance, setActiveMapInstance] =
		useState<mapboxgl.Map | null>(null);
	// Sync `mapInstance.current` with state to trigger re-renders when it updates
	// Callback to sync `mapInstance.current` changes
	const mapRefCallback = useCallback(instance => {
		if (instance) {
			setActiveMapInstance(instance);
		}
	}, []);

	useEffect(() => {
		if (mapInstance?.current) {
			mapRefCallback(mapInstance.current);
		}
	}, [mapInstance?.current, mapRefCallback]);
	const originalMarkerPosition = useRef<mapboxgl.LngLat | null>(null);

	// Detect when `mapInstance.current` changes and call `mapRefCallback`
	useEffect(() => {
		if (mapInstance?.current) {
			mapRefCallback(mapInstance.current);
		}
	}, [mapInstance?.current, mapRefCallback]);
	const isMovingMarkerRef = useRef(false);
	const [disable, setDisable] = useState<boolean>(false);

	// Zoom Controls
	const handleZoomIn = () => {
		if (mapInstance.current) mapInstance.current.zoomIn();
	};

	const handleZoomOut = () => {
		if (mapInstance.current) mapInstance.current.zoomOut();
	};

	const moveMarker = () => {
		if (!activeMapInstance || !blueMarkerRef.current) {
			console.warn('Map instance or marker is not ready.');
			return;
		}

		// Save the original position
		if (blueMarkerRef.current) {
			originalMarkerPosition.current = blueMarkerRef.current.getLngLat();
		}

		isMovingMarkerRef.current = true;
		setDisable(true);

		activeMapInstance.on('mousemove', handleMouseMove);
		activeMapInstance.on('click', handleMapClick);
	};

	const finishMoveMarker = () => {
		if (!activeMapInstance) return;

		activeMapInstance.off('mousemove', handleMouseMove);
		activeMapInstance.off('click', handleMapClick);

		if (originalMarkerPosition.current && blueMarkerRef.current) {
			blueMarkerRef.current.setLngLat(originalMarkerPosition.current); // Reset to original position
		}

		isMovingMarkerRef.current = false;
		setDisable(false);
	};

	const handleMouseMove = (event: mapboxgl.MapMouseEvent) => {
		if (!isMovingMarkerRef.current || !blueMarkerRef.current) return;
		const { lng, lat } = event.lngLat;
		blueMarkerRef.current.setLngLat([lng, lat]);
	};

	const handleMapClick = (event: mapboxgl.MapMouseEvent) => {
		if (!isMovingMarkerRef.current || !blueMarkerRef.current) return;

		const { lng, lat } = event.lngLat;
		blueMarkerRef.current.setLngLat([lng, lat]);
		setQuery({ ...query, coordinates: JSON.stringify([lng, lat]) });
		finishMoveMarker();
	};

	const updateDonutShape = (
		map: mapboxgl.Map,
		coordinates: number[],
		maxRadius: number,
		minRadius: number
	) => {
		const outerCircle = turf.circle(coordinates, maxRadius, {
			units: 'miles'
		});
		const innerCircle = turf.circle(coordinates, minRadius, {
			units: 'miles'
		});
		const donutShape =
			turf.difference(outerCircle, innerCircle) || outerCircle;

		const donutSource = map.getSource(
			'donut-source'
		) as mapboxgl.GeoJSONSource;
		if (donutSource) {
			donutSource.setData(donutShape as GeoJSON.Feature);
		} else {
			map.addSource('donut-source', {
				type: 'geojson',
				data: donutShape as GeoJSON.Feature
			});

			map.addLayer({
				id: 'donut-layer',
				type: 'fill',
				source: 'donut-source',
				paint: {
					'fill-color': 'rgba(24, 189, 78, 0.3)',
					'fill-outline-color': '#2762E9',
					'fill-opacity': 0.3
				}
			});
		}
	};

	const formatBusinessData = business => ({
		_id: business.id || business._id,
		name: business.name || 'Name not available',
		isContacted: business.isContacted || false,
		phone: business.phone?.display || 'Phone not available',
		address: business.address?.address || 'Address not available',
		categories: business.categories?.search?.map(category =>
			category.replace(/&apos;/g, "'")
		) || ['Category not available'],
		website:
			business.website ||
			business.facebook?.website ||
			(business.websites && business.websites.length
				? business.websites[0]
				: 'Website not available'),
		emails: [
			business.email,
			business.facebook?.email,
			business.web?.email,
			...(business.userAddedData
				?.filter(data => data.type === 'email')
				.map(data => data.data) || [])
		].filter(Boolean), // Only include non-empty emails
		yelpUrl: business.yelp?.url || null,
		yelpThumbnail: business.yelp?.thumnnailURL || null,
		yelpAbout: business.yelp?.about || '',
		yelpRating: business.yelp?.rating || 'No rating available',
		socialLinks: {
			facebook: business.facebook?.url || null,
			instagram: business.socials?.instagram || null,
			twitter: business.socials?.twitter || null,
			youtube: business.socials?.youtube || null,
			pinterest: business.socials?.pinterest || null,
			snapchat: business.socials?.snapchat || null,
			tiktok: business.socials?.tiktok || null
		},
		businessData: {
			yearIncorporated:
				business.yearIncorporated ||
				(business.yearsInBusiness
					? new Date().getFullYear() - business.yearsInBusiness
					: 'Not available'),
			numEmployees: business.customData?.numEmployees || 'Not available',
			salesVolume: business.customData?.salesVolume || 'Not available'
		},
		contactNames: (business.contactNames || []).map(contactInfo => ({
			name: contactInfo.name,
			titles: contactInfo.titles || []
		})),
		googleSearchLink: `https://google.com/search?q=${encodeURIComponent(
			`${business.name} ${business.address?.city || ''} ${
				business.address?.state || ''
			}`
		)}`,
		userAddedContacts: (business.userAddedData || [])
			.filter(data => data.type !== 'email')
			.map(data => ({
				type: data.type,
				value: data.data,
				link:
					data.type === 'web-link'
						? data.data.startsWith('http')
							? data.data
							: `https://${data.data}`
						: null
			}))
	});

	useEffect(() => {
		if (!mapInstance.current || selectedView !== 'map') return;

		if (!mapInstance.current.isStyleLoaded()) {
			mapInstance.current.once('style.load', () => {
				createMarkers();
			});
		} else {
			createMarkers();
		}
	}, [businesses, selectedView, mapInstance]);

	const createMarkers = () => {
		if (!mapInstance.current || selectedView !== 'map') return;

		// Remove existing markers to avoid duplicates
		document
			.querySelectorAll('.business-marker')
			.forEach(marker => marker.remove());

		businesses.forEach(business => {
			// console.lo
			const { location } = business.address;
			const coordinates = location?.coordinates;
			if (!coordinates || coordinates.length !== 2) return;

			const img = document.createElement('img');
			// img.style.height = '40px';

			if (business.isContacted) {
				img.src = MarkerContactedRedesign;
			} else {
				img.src = MarkerRedesign;
			}

			// Hover effect for changing the marker
			img.addEventListener('mouseover', () => {
				img.src = business.isContacted
					? MarkerContactedHover
					: MarkerNotContactedHover;
			});

			img.addEventListener('mouseout', () => {
				img.src = business.isContacted
					? MarkerContactedRedesign
					: MarkerRedesign;
			});
			img.style.width = '24px';
			img.style.height = '32px'; // Matches SVG viewBox dimensions

			// Create a new Mapbox marker with a popup
			const popup = new mapboxgl.Popup({
				offset: [37, 76], // [horizontal, vertical] offset. Move to the right size of marker + distance. Start half way vertically. subtract 24 and half marker size
				closeButton: false,
				anchor: 'left' // Ensures popup aligns above the marker
			});

			const marker = new mapboxgl.Marker({
				element: img,
				anchor: 'left'
			})
				.setLngLat(business.address.location.coordinates)
				.setPopup(popup)
				.addTo(mapInstance.current); // Add marker to map

			// Define the onClose function
			const onClose = () => {
				popup.remove();
				if (currentPopup === popup) {
					currentPopup = null;
				}
			};

			const popupContent = createPopupContent(
				formatBusinessData(business),
				onClose
			);
			popup.setDOMContent(popupContent);

			marker.setPopup(popup);

			// Add click event to open this popup and close others
			marker.getElement().addEventListener('click', () => {
				if (currentPopup) {
					currentPopup.remove();
				}
				currentPopup = popup;
				popup.addTo(mapInstance.current);
			});
		});
	};

	const areCoordinatesEqual = (coords1, coords2, decimals = 6) => {
		const factor = Math.pow(10, decimals);
		if (!coords1 || !coords2) return false;
		if (typeof coords1 === 'string') {
			coords1 = JSON.parse(coords1);
		}
		return (
			Math.round(coords1[0] * factor) ===
				Math.round(coords2[0] * factor) &&
			Math.round(coords1[1] * factor) === Math.round(coords2[1] * factor)
		);
	};

	useEffect(() => {
		const parsedCoordinates =
			typeof query?.coordinates === 'string'
				? JSON.parse(query?.coordinates) // Convert string to array
				: query?.coordinates; // Use array directly

		const lngLatMarker = blueMarkerRef.current?.getLngLat();

		if (
			!areCoordinatesEqual(
				mapStateRef.current.center,
				parsedCoordinates
			) &&
			!areCoordinatesEqual(previousCoordinates.current, parsedCoordinates)
		) {
			mapStateRef.current.center = parsedCoordinates;
			mapStateRef.current.zoom = 8;
			previousCoordinates.current = JSON.stringify(parsedCoordinates);
		}
	}, [query.coordinates]);

	// Map Initialization and Cleanup
	useEffect(() => {
		// Parse the coordinates from the query (string or array)
		const parsedCoordinates =
			typeof query?.coordinates === 'string'
				? JSON.parse(query?.coordinates) // Convert string to array
				: query?.coordinates; // Use array directly

		// Check if coordinates are valid (must be an array of length 2)
		if (!Array.isArray(parsedCoordinates) || parsedCoordinates.length !== 2)
			return;

		// Detect if coordinates have changed
		const isCoordinatesChanged =
			JSON.stringify(parsedCoordinates) !== previousCoordinates.current;

		// Only proceed if the 'map' view is selected, the container exists, and the API key is available
		if (selectedView === 'map' && mapBoxRef.current && mapboxAPIKey) {
			mapInstance?.current?.remove(); // Remove existing map
			mapInstance.current = null; // Reset map instance to allow reinitialization

			// Initialize a new map if one doesn't already exist
			if (!mapInstance.current) {
				// Check if mapStateRef is in its default state (e.g., center is [0, 0] and zoom is 8)
				if (
					mapStateRef.current.center[0] === 0 &&
					mapStateRef.current.center[1] === 0 &&
					mapStateRef.current.zoom === 8
				) {
					// Use parsedCoordinates to initialize the map state
					mapStateRef.current = {
						center: parsedCoordinates || [0, 0], // Fallback to default if parsedCoordinates are invalid
						zoom: 8 // Default zoom
					};
				}

				if (
					!areCoordinatesEqual(
						mapStateRef.current.center,
						parsedCoordinates
					) &&
					!areCoordinatesEqual(
						previousCoordinates.current,
						parsedCoordinates
					)
				) {
					mapStateRef.current.center = parsedCoordinates;
				}
				// Create a new Mapbox map with the specified container, style, and initial center
				mapInstance.current = new mapboxgl.Map({
					container: mapBoxRef.current, // HTML element to render the map
					style: 'mapbox://styles/mapbox/dark-v10', // Mapbox style
					center: mapStateRef.current.center,
					zoom: mapStateRef.current.zoom,
					accessToken: mapboxAPIKey // Mapbox API key for authorization
				});

				mapInstance.current.on('load', () => {
					createMarkers();
				});

				// Enable scroll zoom functionality
				mapInstance.current.scrollZoom.enable();

				// Add a marker at the specified coordinates
				blueMarkerRef.current = new mapboxgl.Marker({ draggable: true })
					.setLngLat([parsedCoordinates[0], parsedCoordinates[1]])
					.addTo(mapInstance.current);

				// Add the initial donut shape when the map loads
				mapInstance.current.on('load', () => {
					updateDonutShape(
						mapInstance.current,
						parsedCoordinates,
						query.maxDistance,
						query.minDistance
					);
				});
				// Update the stored zoom and center whenever the map changes
				const updateMapState = () => {
					if (mapInstance.current) {
						mapStateRef.current.zoom =
							mapInstance.current.getZoom();
						mapStateRef.current.center = mapInstance.current
							.getCenter()
							.toArray();
					}
				};
				blueMarkerRef.current.on('dragend', () => {
					const currentLocation = blueMarkerRef.current.getLngLat();

					if (
						!Map.currentMask ||
						!inside(
							currentLocation.toArray(),
							Map.currentMask as Feature<Polygon | MultiPolygon>
						)
					) {
						const queryNew = fromJS(query).toJS();

						delete queryNew.coordinates;

						queryNew.coordinates = JSON.stringify([
							Number(Number(currentLocation.lng).toFixed(4)),
							Number(Number(currentLocation.lat).toFixed(4))
						]);

						queryNew.limit = 50;

						setQuery(queryNew);
					}
				});

				mapInstance.current.on('moveend', updateMapState);
				mapInstance.current.on('zoomend', updateMapState);

				setActiveMapInstance(mapInstance.current); // Sync state immediately
			} else {
				// If the map already exists, update its center to the new coordinates
				mapInstance.current.setCenter(parsedCoordinates);

				// Update the donut shape to reflect the new radius or coordinates
				updateDonutShape(
					mapInstance.current,
					parsedCoordinates,
					query.maxDistance,
					query.minDistance
				);
			}
		}

		// Cleanup: Remove the map instance when switching away from the 'map' view or unmounting
		return () => {
			if (mapInstance.current && selectedView !== 'map') {
				mapInstance.current.remove(); // Remove the map instance
				mapInstance.current = null; // Reset to avoid memory leaks
			}
		};
	}, [
		mapBoxRef.current, // trigger when the ref changes
		selectedView, // Watch for changes in view (e.g., switching to/from 'map')
		query.coordinates, // Watch for changes in location coordinates
		query.maxDistance, // Watch for changes in the max radius of the donut
		query.minDistance // Watch for changes in the min radius of the donut
	]);

	return (
		<div className="relative h-full w-full">
			{/* Map Container */}
			<div
				ref={mapBoxRef}
				className="map-display-container"
				style={{ width: '100%', height: '100%' }}
			/>

			{/* Zoom Controls Outside Map Container */}
			<div className="map-button-container absolute right-4 top-4 z-30">
				{/* Toggle Button */}
				{disable ? (
					<button
						onClick={finishMoveMarker}
						type="button"
						className="businessMap-controls--finish move-marker-button flex flex-col items-center justify-center rounded text-xs font-medium"
					>
						<ClearIcon
							width={11}
							height={14}
							className="flex self-center"
						/>
						Cancel
					</button>
				) : (
					<>
						<button
							onClick={moveMarker}
							type="button"
							className="businessMap-controls--move move-marker-button flex flex-col items-center justify-center rounded text-xs font-medium"
						>
							<MarkerChange
								className="flex self-center"
								width={11}
								height={15}
							/>
							Move
						</button>
					</>
				)}
				<>
					<div className="map-segmentation-line" />
					<button
						onClick={handleZoomIn}
						type="button"
						className="map-button-style"
					>
						+
					</button>

					<button
						onClick={handleZoomOut}
						type="button"
						className="map-button-style map-buttons-close"
					>
						−
					</button>
				</>
			</div>
		</div>
	);
};

export default SearchBusinessesMap;
